import { useState } from 'react';
import type { CashRegisterDto } from '@barbeiropro/contracts';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { FormModal } from '@/components/shared/form-modal';
import { useTableParams } from '@/components/shared/use-table-params';
import { fmtDateTime, money } from '@/lib/format';
import { ExportMenu } from '@/components/shared/export-menu';
import { useTimezone } from '@/lib/org';
import { PeriodFilter, usePeriodParams } from '../period';
import { cn } from '@/lib/utils';
import { useRegisterDetail, useRegisters } from '../api';
import { signed } from '../labels';
import { diffClass } from './close-register-modal';
import { MovementsTable } from './movements-table';

function RegisterDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useRegisterDetail(id);
  return (
    <FormModal open onOpenChange={(v) => !v && onClose()} size="lg" title="Detalhes do caixa" description={q.data ? `Unidade ${q.data.branchName}` : undefined}>
      {q.isLoading ? (
        <LoadingState />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.data ? (
        <div className="grid gap-4">
          {q.data.notes && <p className="text-sm text-muted-foreground">Observações: {q.data.notes}</p>}
          <MovementsTable movements={q.data.movements} />
        </div>
      ) : null}
    </FormModal>
  );
}

export function HistoryTable({ branchId }: { branchId: string }) {
  const tz = useTimezone();
  const params = useTableParams({ sort: 'openedAt', order: 'desc' });
  const period = usePeriodParams();
  const filters = { branchId, status: 'closed' as const, from: period.from, to: period.to };
  const q = useRegisters({ ...params.query, ...filters });
  const [detail, setDetail] = useState<string | null>(null);

  const columns: DataColumn<CashRegisterDto>[] = [
    { header: 'Abertura', accessorFn: (r) => r.openedAt, cell: ({ row }) => <span className="tabular">{fmtDateTime(row.original.openedAt, tz)}</span>, meta: { sortKey: 'openedAt' } },
    { header: 'Fechamento', accessorFn: (r) => r.closedAt, cell: ({ row }) => <span className="tabular">{row.original.closedAt ? fmtDateTime(row.original.closedAt, tz) : '-'}</span>, meta: { sortKey: 'closedAt' } },
    { header: 'Responsável', accessorFn: (r) => r.closedByName ?? r.openedByName, cell: ({ row }) => row.original.closedByName ?? row.original.openedByName ?? '-' },
    { header: 'Abertura (valor)', accessorFn: (r) => r.openingAmountCents, cell: ({ row }) => money(row.original.openingAmountCents), meta: { align: 'right' } },
    { header: 'Esperado', accessorFn: (r) => r.expectedCashCents, cell: ({ row }) => money(row.original.expectedCashCents), meta: { align: 'right' } },
    { header: 'Contado', accessorFn: (r) => r.countedAmountCents, cell: ({ row }) => money(row.original.countedAmountCents), meta: { align: 'right' } },
    {
      header: 'Diferença',
      accessorFn: (r) => r.differenceCents,
      cell: ({ row }) => <span className={cn('font-medium', diffClass(row.original.differenceCents))}>{signed(row.original.differenceCents ?? 0, money)}</span>,
      meta: { sortKey: 'differenceCents', align: 'right' },
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={q.data}
        params={params}
        isLoading={q.isLoading}
        error={q.error}
        onRetry={() => q.refetch()}
        searchPlaceholder="Buscar"
        filters={<PeriodFilter from={period.from} to={period.to} tz={period.tz} onChange={(v) => { period.set(v); void params.set({ page: 1 }); }} />}
        toolbar={
          <ExportMenu
            disabled={!q.data?.total}
            options={[
              { label: 'Planilha (XLSX)', path: '/cash-registers/export', query: { ...filters, format: 'xlsx' }, filename: 'historico-de-caixas.xlsx' },
              { label: 'PDF', path: '/cash-registers/export', query: { ...filters, format: 'pdf' }, filename: 'historico-de-caixas.pdf' },
            ]}
          />
        }
        onRowClick={(r) => setDetail(r.id)}
        empty={{ title: 'Nenhum caixa fechado neste período', description: 'Escolha outro período ou feche um caixa para ele aparecer aqui.' }}
      />
      {detail && <RegisterDetailModal id={detail} onClose={() => setDetail(null)} />}
    </>
  );
}
